import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Avatar,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetCategoriesQuery,
} from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: product, isLoading: loadingProduct } = useGetProductQuery(id, {
    skip: !isEdit,
  });
  const { data: categoriesData } = useGetCategoriesQuery(false);
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    barcode: '',
    categoryId: '',
    brand: '',
    costPrice: 0,
    wholesalePrice: 0,
    sellingPrice: 0,
    openingStock: '',
    maxDiscountType: '',
    maxDiscountPercent: '',
    maxDiscountAmount: '',
    isActive: true,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (product) {
      setFormData({
        sku: product.sku || '',
        name: product.name || '',
        description: product.description || '',
        barcode: product.barcode || '',
        categoryId: product.categoryId || '',
        brand: product.brand || '',
        costPrice: product.costPrice || 0,
        wholesalePrice: product.wholesalePrice || 0,
        sellingPrice: product.sellingPrice || 0,
        openingStock: product.openingStock != null ? String(product.openingStock) : '',
        maxDiscountType: product.maxDiscountType === 'percent' || product.maxDiscountType === 'amount' ? product.maxDiscountType : '',
        maxDiscountPercent: product.maxDiscountPercent != null ? String(product.maxDiscountPercent) : '',
        maxDiscountAmount: product.maxDiscountAmount != null ? String(product.maxDiscountAmount) : '',
        isActive: product.isActive !== undefined ? product.isActive : true,
      });
      // Set image preview if product has an image
      if (product.imageUrl) {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const baseUrl = apiUrl.replace('/api', '');
        setImagePreview(`${baseUrl}${product.imageUrl}`);
      } else {
        setImagePreview(null);
      }
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.sku || !formData.sku.trim()) {
      alert('SKU is required');
      return;
    }
    if (!formData.name || !formData.name.trim()) {
      alert('Product name is required');
      return;
    }
    
    try {
      // Create FormData if image is being uploaded, otherwise use regular object
      let submitData;
      
      if (imageFile) {
        // Use FormData when uploading an image
        submitData = new FormData();
        submitData.append('sku', formData.sku.trim());
        submitData.append('name', formData.name.trim());
        submitData.append('description', formData.description || '');
        submitData.append('barcode', formData.barcode || '');
        submitData.append('categoryId', formData.categoryId ? formData.categoryId : '');
        submitData.append('brand', formData.brand || '');
        submitData.append('costPrice', parseFloat(formData.costPrice) || 0);
        submitData.append('wholesalePrice', parseFloat(formData.wholesalePrice) || 0);
        submitData.append('sellingPrice', parseFloat(formData.sellingPrice) || 0);
        if (formData.openingStock !== '' && formData.openingStock != null) {
          submitData.append('openingStock', parseFloat(formData.openingStock) || 0);
        }
        if (formData.maxDiscountType === 'percent' || formData.maxDiscountType === 'amount') {
          submitData.append('maxDiscountType', formData.maxDiscountType);
          if (formData.maxDiscountType === 'percent' && formData.maxDiscountPercent !== '') {
            submitData.append('maxDiscountPercent', formData.maxDiscountPercent);
          }
          if (formData.maxDiscountType === 'amount' && formData.maxDiscountAmount !== '') {
            submitData.append('maxDiscountAmount', formData.maxDiscountAmount);
          }
        }
        submitData.append('isActive', formData.isActive);
        submitData.append('image', imageFile);
      } else {
        // Use regular JSON object when no image
        submitData = {
          sku: formData.sku.trim(),
          name: formData.name.trim(),
          description: formData.description || '',
          barcode: formData.barcode || '',
          categoryId: formData.categoryId || null,
          brand: formData.brand || '',
          costPrice: parseFloat(formData.costPrice) || 0,
          wholesalePrice: parseFloat(formData.wholesalePrice) || 0,
          sellingPrice: parseFloat(formData.sellingPrice) || 0,
          openingStock: formData.openingStock !== '' && formData.openingStock != null ? parseFloat(formData.openingStock) : undefined,
          maxDiscountType: formData.maxDiscountType === 'percent' || formData.maxDiscountType === 'amount' ? formData.maxDiscountType : undefined,
          maxDiscountPercent: formData.maxDiscountType === 'percent' && formData.maxDiscountPercent !== '' ? formData.maxDiscountPercent : undefined,
          maxDiscountAmount: formData.maxDiscountType === 'amount' && formData.maxDiscountAmount !== '' ? formData.maxDiscountAmount : undefined,
          isActive: formData.isActive,
        };
      }

      if (isEdit) {
        await updateProduct(
          imageFile ? { id, body: submitData } : { id, ...submitData }
        ).unwrap();
      } else {
        await createProduct(submitData).unwrap();
      }
      navigate('/products');
    } catch (error) {
      console.error('Error saving product:', error);
      alert(error?.data?.message || 'Error saving product');
    }
  };

  if (loadingProduct) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEdit ? 'Edit Product' : 'New Product'}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" fontWeight="medium">
                  Product Image
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {imagePreview && (
                    <Avatar
                      src={imagePreview}
                      alt="Product preview"
                      sx={{ width: 120, height: 120 }}
                      variant="rounded"
                    />
                  )}
                  <Button variant="outlined" component="label">
                    {imagePreview ? 'Change Image' : 'Upload Image'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </Button>
                  {imagePreview && (
                    <Button
                      variant="text"
                      color="error"
                      onClick={() => {
                        setImageFile(null);
                        setImagePreview(null);
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Supported formats: JPEG, PNG, GIF, WebP (Max 5MB)
                </Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Category"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                helperText={
                  formData.categoryId
                    ? categoriesData?.find((c) => c.id === formData.categoryId)?.parent
                        ? `Main: ${categoriesData.find((c) => c.id === formData.categoryId)?.parent?.name} > Sub: ${categoriesData.find((c) => c.id === formData.categoryId)?.name}`
                        : categoriesData?.find((c) => c.id === formData.categoryId)?.name
                    : 'Select a category (preferably a subcategory)'
                }
              >
                {/* Show subcategories first */}
                {categoriesData
                  ?.filter((category) => category.parentId !== null)
                  .map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.parent?.name
                        ? `${category.parent.name} > ${category.name}`
                        : category.name}
                    </MenuItem>
                  ))}
                {/* Then show main categories (if no subcategories available) */}
                {categoriesData?.filter((category) => category.parentId === null).length > 0 &&
                  categoriesData?.filter((category) => category.parentId !== null).length === 0 &&
                  categoriesData
                    ?.filter((category) => category.parentId === null)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name} (Main Category)
                      </MenuItem>
                    ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Cost Price (${currency})`}
                name="costPrice"
                type="number"
                value={formData.costPrice}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Wholesale Price (${currency})`}
                name="wholesalePrice"
                type="number"
                value={formData.wholesalePrice}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={`Selling Price (${currency})`}
                name="sellingPrice"
                type="number"
                value={formData.sellingPrice}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            {!isEdit && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Opening Stock"
                  name="openingStock"
                  type="number"
                  value={formData.openingStock}
                  onChange={handleChange}
                  placeholder="Initial quantity (applied on create)"
                  helperText="Optional. Sets initial stock at Main Warehouse when creating the product."
                  inputProps={{ step: '0.01', min: '0' }}
                />
              </Grid>
            )}
            {isEdit && formData.openingStock !== '' && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Opening Stock (reference)"
                  name="openingStock"
                  value={formData.openingStock}
                  InputProps={{ readOnly: true }}
                  helperText="Opening stock is set at creation; edit stock via Inventory."
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Max discount (sales) – product override
              </Typography>
              <RadioGroup
                row
                value={formData.maxDiscountType || 'none'}
                onChange={(e) => setFormData((prev) => ({ ...prev, maxDiscountType: e.target.value === 'none' ? '' : e.target.value }))}
              >
                <FormControlLabel value="none" control={<Radio />} label="Use company default" />
                <FormControlLabel value="percent" control={<Radio />} label="Percentage" />
                <FormControlLabel value="amount" control={<Radio />} label="Amount per line" />
              </RadioGroup>
              {formData.maxDiscountType === 'percent' && (
                <TextField
                  fullWidth
                  label="Max discount %"
                  name="maxDiscountPercent"
                  type="number"
                  value={formData.maxDiscountPercent}
                  onChange={handleChange}
                  placeholder="e.g. 15"
                  helperText="Product-specific max discount %."
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={{ maxWidth: 400, mt: 1 }}
                />
              )}
              {formData.maxDiscountType === 'amount' && (
                <TextField
                  fullWidth
                  label={`Max discount amount (${currency})`}
                  name="maxDiscountAmount"
                  type="number"
                  value={formData.maxDiscountAmount}
                  onChange={handleChange}
                  placeholder="e.g. 5"
                  helperText="Product-specific max discount amount per line."
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ maxWidth: 400, mt: 1 }}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button type="submit" variant="contained">
                  {isEdit ? 'Update' : 'Create'}
                </Button>
                <Button variant="outlined" onClick={() => navigate('/products')}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default ProductForm;
